/**
 * MLB postseason games as the partners name them, in plain words — one rule for the build,
 * the edge, the radar and the site.
 *
 * WHY (2026-10-18 audit): the World Series pages read "TBD at Brewers - World Series (Gm 1,
 * HG 1)" in the heading, the title and the opening line, and Ticketmaster's placeholders
 * ("TBD at Milwaukee Brewers: World Series (Home Game 1)", "World Series Home Game 1" for a club
 * already knocked out) became pages of their own. The partners know these games before the
 * matchup is set; we say what is known and what is not.
 *
 *   parsePostseason   round, game number, home-game number, home team, visitor (null = not
 *                     decided), whether the game is played only if the series is not decided earlier
 *   postseasonLabel   "World Series Game 1: Brewers home game", "World Series Game 3: Brewers at
 *                     Guardians". The part before the colon is the occasion, so the edge's Event
 *                     schema (functions/_middleware.ts strips a "Prefix: ") names only real clubs.
 *                     A label parses back to the same game, so labelling twice changes nothing.
 *   isPostseasonPlaceholder   any sport: a playoff game listed for a club before its matchup is
 *                     set, or played only if needed — the radar publishes no page for it
 *                     (workers/radar), the long tail retires an MLB one (placeholderTarget says
 *                     where its visitors go); postseasonRound tells two partners' listings of one
 *                     game apart from another round's
 *   pennantPending    World Series games whose home team has not clinched its pennant
 *   confirmedClubs, seasonClub   the clubs known to be in a season's World Series, handed from
 *                     one build to the next (a club never un-clinches)
 *   knownOpponents    World Series games that name no visitor but whose opponent is known: the
 *                     other league's champion ("TBD at Rays" once the Brewers are in)
 *   markWorldSeries   one build's flags on its games (scripts/gen-catalog.mjs): pennantPending
 *                     and wsOpponent, from the clubs earlier builds knew to be in
 *   postseasonDoubts  what is not certain about a game, for its page (src/lib/notices.ts)
 *   exchangeSaysPlayed   the exchange's own name says a game is certain (no "(If Necessary)")
 *   outOfSeries       World Series games of a club that is out, once both pennants are known
 *   seriesRecord      the series page's games: today's listings plus every game recorded earlier
 *                     this season (a game played stays on the page with its last price)
 *   missingGames      games of the series nobody lists yet, and whose ballpark they are at
 *   mlbNickname, mlbLeague   "Milwaukee Brewers" = "Brewers", in the National League
 *
 * Names are the partners' own data and stay English, like every event name. Ids and slugs never
 * change: a slug comes from the teams, not from the name (shared/slug.mjs).
 */

/** @typedef {'World Series' | 'ALCS' | 'NLCS' | 'ALDS' | 'NLDS' | 'AL Wild Card Series' | 'NL Wild Card Series'} Round */

/**
 * @typedef {{
 *   round: Round,
 *   game: number | null,
 *   homeGame: number | null,
 *   home: string,
 *   away: string | null,
 *   ifNecessary: boolean,
 * }} Postseason
 */

/** Games in each round's series: the World Series and the LCS are best of seven (2-3-2), the
 *  division series best of five (2-2-1), the wild-card series best of three (all at one park). */
/** @type {Record<Round, number>} */
const BEST_OF = { 'World Series': 7, ALCS: 7, NLCS: 7, ALDS: 5, NLDS: 5, 'AL Wild Card Series': 3, 'NL Wild Card Series': 3 };

const ROUND_SRC =
  "World Series|ALCS|NLCS|ALDS|NLDS|(?:AL|NL) Wild Card(?: Series)?|(?:American|National) League (?:Championship|Division|Wild Card) Series";

/** @param {string} s @returns {Round | null} */
const roundOf = (s) => {
  const t = s.trim().toLowerCase().replace(/\s+/g, ' ');
  if (t === 'world series') return 'World Series';
  if (t === 'alcs' || t === 'american league championship series') return 'ALCS';
  if (t === 'nlcs' || t === 'national league championship series') return 'NLCS';
  if (t === 'alds' || t === 'american league division series') return 'ALDS';
  if (t === 'nlds' || t === 'national league division series') return 'NLDS';
  if (/^(al|american league) wild card( series)?$/.test(t)) return 'AL Wild Card Series';
  if (/^(nl|national league) wild card( series)?$/.test(t)) return 'NL Wild Card Series';
  return null;
};

/** The thirty clubs by nickname, as SeatGeek writes them ("Brewers"); Ticketmaster's full names
 *  ("Milwaukee Brewers") end in one. */
const AL = ['Orioles', 'Red Sox', 'Yankees', 'Rays', 'Blue Jays', 'White Sox', 'Guardians', 'Tigers', 'Royals', 'Twins', 'Astros', 'Angels', 'Athletics', 'Mariners', 'Rangers'];
const NL = ['Braves', 'Marlins', 'Mets', 'Phillies', 'Nationals', 'Cubs', 'Reds', 'Brewers', 'Pirates', 'Cardinals', 'Diamondbacks', 'Rockies', 'Dodgers', 'Padres', 'Giants'];
const NICKNAMES = [...AL, ...NL].sort((a, b) => b.length - a.length);

/** "Milwaukee Brewers" / "Brewers" → "Brewers"; null for anything that is not an MLB club.
 *  @param {string | null | undefined} team */
export const mlbNickname = (team) => {
  const t = String(team ?? '').trim();
  return NICKNAMES.find((n) => t === n || t.endsWith(` ${n}`)) ?? null;
};

/** Which league an MLB club plays in ("AL" / "NL"), null when it is not one.
 *  @param {string | null | undefined} team @returns {'AL' | 'NL' | null} */
export const mlbLeague = (team) => {
  const n = mlbNickname(team);
  return n ? (AL.includes(n) ? 'AL' : 'NL') : null;
};

/** A side of a matchup: null when it is not decided ("TBD"), else the name as the partner wrote it.
 *  @param {string} s */
const side = (s) => {
  const t = s.trim().replace(/\s+/g, ' ');
  return /^(?:tb[ad]|to be (?:determined|announced)|opponent tb[ad])$/i.test(t) ? null : t;
};

/** A plausible club name: words only, no numbers or brackets (those belong to the game details). */
const NAME_OK = /^[A-Za-z.'&\- ]{2,40}$/;

/** "(Time: TBD)" is only an unknown start time (scripts/gen-longtail.mjs maybeGame). */
const stripTime = (/** @type {string} */ s) => s.replace(/\(?\btime:?\s*tb[ad]\b\)?/gi, ' ');

/** Game details from the brackets: "Gm 1, HG 1", "Game 5, HG 3", "HG 4", "Home Game 3, If Necessary".
 *  "(Formerly Gm 5)" names the number the game had before a reshuffle, not this game's.
 *  @param {string} s */
const details = (s) => {
  let t = ` ${s} `.replace(/\bformerly\b[^,)]*/gi, ' ');
  const hg = t.match(/\b(?:hg|home\s+game)\s*#?\s*(\d{1,2})\b/i);
  if (hg) t = t.replace(hg[0], ' ');
  const gm = t.match(/\b(?:gm|game)\s*#?\s*(\d{1,2})\b/i);
  return { game: gm ? Number(gm[1]) : null, homeGame: hg ? Number(hg[1]) : null, ifNecessary: /\bif\s+necessary\b/i.test(s) };
};

/**
 * Is a game of this number played only if the series is not decided earlier? From the round's
 * format alone: true / false, or null when the number cannot tell (a division series' home game 2
 * is game 2 for one club and game 4 for the other).
 * @param {Round} round @param {number | null} game @param {number | null} homeGame
 * @returns {boolean | null}
 */
const impliedIfNecessary = (round, game, homeGame) => {
  const n = BEST_OF[round];
  if (game) return game > Math.ceil(n / 2);
  if (!homeGame) return null;
  // best of 7 (2-3-2): home games 1-2 are games 1-4; home game 3 is game 5 or 6, home game 4 game 7
  if (n === 7) return homeGame >= 3;
  // best of 5 (2-2-1): home game 1 is game 1 or 3; home game 2 is game 2 or 4; home game 3 is game 5
  if (n === 5) return homeGame >= 3 ? true : homeGame === 1 ? false : null;
  // best of 3, all at one park: home game n is game n
  return homeGame >= 3;
};

/** The most home games one club can have in a series of n games (2-3-2, 2-2-1, all at one park). */
/** @type {Record<number, number>} */
const MAX_HOME = { 7: 4, 5: 3, 3: 3 };

/** @param {Round} round @param {number | null} game @param {number | null} homeGame @param {string | null} home @param {string | null} away @param {boolean} explicit
 *  @returns {Postseason | null} */
const make = (round, game, homeGame, home, away, explicit) => {
  const n = BEST_OF[round];
  if (!home || !NAME_OK.test(home) || (away !== null && !NAME_OK.test(away))) return null;
  // The home team is a club, or this is not a game we can read ("World Series: Tampa Bay Rays vs.
  // TBD - Home Game 1" once read as a home team "Tampa Bay Rays vs. TBD -"). A visitor that is no
  // club ("AL Champion") is a visitor not decided yet: it never counts as a club that is in.
  if (!mlbNickname(home)) return null;
  const visitor = away !== null && mlbNickname(away) ? away : null;
  if (game !== null && (game < 1 || game > n)) return null;
  if (homeGame !== null && (homeGame < 1 || homeGame > MAX_HOME[n])) return null;
  if (game === null && homeGame === null) return null;
  return { round, game, homeGame, home, away: visitor, ifNecessary: explicit || impliedIfNecessary(round, game, homeGame) === true };
};

// Partner names. SeatGeek: "Away at Home - Round (Gm 1, HG 1)". Ticketmaster: "TBD at Home:
// Round (Home Game 3, If Necessary)", "Home vs. Away: Round (…)", and "Round Home Game 1" with the
// club as the event's performer.
const SG_TM = new RegExp(`^(.+?)\\s+(at|vs\\.?|v\\.?)\\s+(.+?)\\s*(?:\\s-\\s|:\\s*)(${ROUND_SRC})\\s*((?:\\([^)]*\\)\\s*)+)$`, 'i');
const TM_SOLO = new RegExp(`^(${ROUND_SRC})\\s+home\\s+game\\s+(\\d{1,2})\\b\\s*(.*)$`, 'i');
// The exchange's names (Ticket Squeeze and TicketNetwork share them; HelloTickets shows the same
// games): "Round: Home vs. Away - Home Game 1", then the series game number once the exchange
// knows it and "(If Necessary)" — or "(Date: TBD - If Necessary)" — while the game may not be
// played: "World Series: Tampa Bay Rays vs. TBD - Home Game 1 (Date: TBD - If Necessary)",
// "World Series: Tampa Bay Rays vs. Milwaukee Brewers - Home Game 1, Series Game 3 (If
// Necessary)", "American League Championship Series: Tampa Bay Rays vs. Cleveland Guardians -
// Home Game 3, Series Game 6". The home club comes first. Nothing else may follow ("… - Home Game
// 1 Parking" is not read).
const EXCHANGE = new RegExp(
  `^(${ROUND_SRC}):\\s+(.+?)\\s+(?:vs\\.?|v\\.?)\\s+(.+?)\\s+-\\s+home\\s+game\\s+(\\d{1,2})(?:\\s*,\\s*(?:series\\s+)?game\\s+(\\d{1,2}))?(?:\\s*\\(\\s*(?:date:\\s*tb[ad]\\s*-\\s*)?(if\\s+necessary)\\s*\\))?$`,
  'i'
);
// HelloTickets' other form: "TBD at Toronto Blue Jays (World Series, Home Game 1)", "TBD at
// Minnesota Twins (ALCS Home Game 2)".
const HT_PAREN = new RegExp(`^(.+?)\\s+(at|vs\\.?|v\\.?)\\s+(.+?)\\s*\\((${ROUND_SRC}),?\\s+home\\s+game\\s+(\\d{1,2})\\)$`, 'i');
// Our own labels (postseasonLabel), so a name labelled once parses to the same game again.
const L_GAME_HOME = new RegExp(`^(${ROUND_SRC}) Game (\\d{1,2}): (.+?) home game( \\(if necessary\\))?$`, 'i');
const L_GAME_AT = new RegExp(`^(${ROUND_SRC}) Game (\\d{1,2}): (.+?) at (.+?)( \\(if necessary\\))?$`, 'i');
const L_HG_HOME = new RegExp(`^(${ROUND_SRC}): (.+?) home game (\\d{1,2})( \\(if necessary\\))?$`, 'i');
const L_HG_AT = new RegExp(`^(${ROUND_SRC}) home game (\\d{1,2}): (.+?) at (.+?)( \\(if necessary\\))?$`, 'i');

/**
 * An MLB postseason game from its name, or null when the name is not one we can read with
 * certainty (anything unread keeps the partner's own name).
 * @param {string | null | undefined} name
 * @param {{ artist?: string | null } | null} [ctx] the event's performer: Ticketmaster names some
 *   games only "World Series Home Game 1" and lists the club as the performer
 * @returns {Postseason | null}
 */
export const parsePostseason = (name, ctx = null) => {
  const raw = stripTime(String(name ?? '')).replace(/\s+/g, ' ').trim();
  if (!raw || raw.length > 160) return null;
  let m = raw.match(SG_TM);
  if (m) {
    const round = roundOf(m[4]);
    if (!round) return null;
    const d = details(m[5]);
    const vs = !/^at$/i.test(m[2]);
    // "Away at Home"; "Home vs. Away"
    const home = side(vs ? m[1] : m[3]);
    const away = side(vs ? m[3] : m[1]);
    return home ? make(round, d.game, d.homeGame, home, away, d.ifNecessary) : null;
  }
  // before our own labels: their "Round: Club home game 1" also matches the exchange's words
  if ((m = raw.match(EXCHANGE))) {
    const round = roundOf(m[1]);
    const home = side(m[2]);
    return round && home ? make(round, m[5] ? Number(m[5]) : null, Number(m[4]), home, side(m[3]), !!m[6]) : null;
  }
  if ((m = raw.match(HT_PAREN))) {
    const round = roundOf(m[4]);
    const vs = !/^at$/i.test(m[2]);
    const home = side(vs ? m[1] : m[3]);
    return round && home ? make(round, null, Number(m[5]), home, side(vs ? m[3] : m[1]), false) : null;
  }
  if ((m = raw.match(L_GAME_HOME))) {
    const round = roundOf(m[1]);
    return round ? make(round, Number(m[2]), null, m[3], null, !!m[4]) : null;
  }
  if ((m = raw.match(L_GAME_AT))) {
    const round = roundOf(m[1]);
    return round ? make(round, Number(m[2]), null, m[4], m[3], !!m[5]) : null;
  }
  if ((m = raw.match(L_HG_HOME))) {
    const round = roundOf(m[1]);
    return round ? make(round, null, Number(m[3]), m[2], null, !!m[4]) : null;
  }
  if ((m = raw.match(L_HG_AT))) {
    const round = roundOf(m[1]);
    return round ? make(round, null, Number(m[2]), m[4], m[3], !!m[5]) : null;
  }
  // last: "World Series Home Game 1" means a game only with an MLB club as the performer
  if ((m = raw.match(TM_SOLO)) && !m[3].startsWith(':')) {
    const round = roundOf(m[1]);
    const home = ctx?.artist ? String(ctx.artist).trim() : '';
    if (!round || !mlbNickname(home)) return null;
    return make(round, null, Number(m[2]), home, null, /\bif\s+necessary\b/i.test(m[3]));
  }
  return null;
};

/**
 * A game the exchange (Ticket Squeeze and TicketNetwork) names as certain to be played: its own
 * form with a series game number and no "(If Necessary)". The exchange marks every game that may
 * not be played and drops the mark once a game is certain (ALCS Game 6 at 3-2: "… - Home Game 3,
 * Series Game 6"), so for its listings the mark, not the number, says it (review, 2026-10-19: the
 * number rule put "played only if the series isn't decided earlier" on that game). HelloTickets
 * copies the exchange's form but never writes the mark: never read its names this way, nor
 * SeatGeek's or Ticketmaster's (scripts/gen-longtail.mjs sets `sure` on exchange pages only).
 * @param {string | null | undefined} name
 */
export const exchangeSaysPlayed = (name) => {
  const m = stripTime(String(name ?? '')).replace(/\s+/g, ' ').trim().match(EXCHANGE);
  return !!m && !!roundOf(m[1]) && !!m[5] && !m[6];
};

/**
 * The plain name of a postseason game: "World Series Game 1: Brewers home game" while the
 * visitor is not decided, "World Series Game 3: Brewers at Guardians" once it is, "NLCS:
 * Dodgers home game 4" when only the home-game number is known. "(if necessary)" only where the
 * number alone does not already say it.
 * @param {Postseason} p
 */
export const formatPostseason = (p) => {
  const implied = impliedIfNecessary(p.round, p.game, p.homeGame);
  const tail = p.ifNecessary && implied !== true ? ' (if necessary)' : '';
  if (p.game) return `${p.round} Game ${p.game}: ${p.away ? `${p.away} at ${p.home}` : `${p.home} home game`}${tail}`;
  return p.away ? `${p.round} home game ${p.homeGame}: ${p.away} at ${p.home}${tail}` : `${p.round}: ${p.home} home game ${p.homeGame}${tail}`;
};

/**
 * The plain name for an event, or null to keep its own. A game listed under its club as the
 * performer keeps the partner's words ("Chicago White Sox" over "World Series Home Game 1"): the
 * page already leads with the club.
 * @param {string | null | undefined} name
 * @param {{ artist?: string | null } | null} [ctx]
 */
export const postseasonLabel = (name, ctx = null) => {
  if (ctx?.artist) return null;
  const p = parsePostseason(name, ctx);
  return p ? formatPostseason(p) : null;
};

/**
 * A postseason game as a buyer names it inside a sentence ("How much are World Series Game 1
 * tickets at American Family Field?"): the game, with the venue and date said around it; "Dodgers
 * World Series home game 4" when only the home-game number is known. Null for any other event,
 * and for a game listed under its club as the performer.
 * @param {string | null | undefined} name
 * @param {{ artist?: string | null } | null} [ctx]
 */
export const postseasonShortName = (name, ctx = null) => {
  if (ctx?.artist) return null;
  const p = parsePostseason(name, ctx);
  return p ? (p.game ? `${p.round} Game ${p.game}` : `${p.home} ${p.round} home game ${p.homeGame}`) : null;
};

/** A playoff round named anywhere in a title, any sport ("Final Four" is not one: it is played
 *  whoever reaches it). */
const ROUND_WORDS = /\b(?:world series|alcs|nlcs|alds|nlds|wild card|division series|championship series|postseason|playoffs?|stanley cup|finals)\b/i;
/** Played only if needed, or a SeatGeek game number of an unset matchup (scripts/gen-longtail.mjs CONTINGENT). */
const MAYBE = /\(HG \d+\)|\bGm \d+\b|if necessary/i;
/** One side named, the other not: a club's home (or road) game against whoever gets there. Both
 *  sides TBD is a game that is played whoever reaches it (a final at a neutral site).
 *  @param {string} s */
const oneSideTbd = (s) => {
  const left = s.match(/\bTB[AD]\s+(?:at|vs\.?|v\.?)\s+(\S+)/i);
  if (left) return !/^TB[AD]\b/i.test(left[1]);
  const right = s.match(/(\S+)\s+(?:at|vs\.?|v\.?)\s+TB[AD]\b/i);
  return !!right && !/^TB[AD]$/i.test(right[1]);
};

/**
 * A playoff game a partner lists for a club before its matchup is set, or one played only if
 * needed: no page of its own (workers/radar skips it, scripts/gen-longtail.mjs retires an MLB
 * one). "(Time: TBD)" is only an unknown start time; a numbered home game with both teams named
 * ("WNBA Finals: Golden State Valkyries vs. Atlanta Dream - Home Game 2") is a real game, and so
 * is a final with both sides still TBD — it is played whoever gets there.
 * @param {string | null | undefined} name
 */
export const isPostseasonPlaceholder = (name) => {
  const s = stripTime(String(name ?? ''));
  if (!ROUND_WORDS.test(s)) return false;
  if (MAYBE.test(s) || oneSideTbd(s)) return true;
  return /\bhome game\s*#?\s*\d+/i.test(s) && !/\s(?:vs\.?|v\.?|at)\s/i.test(s);
};

const ROUND_ANY = new RegExp(`\\b(${ROUND_SRC})\\b`, 'i');
/**
 * The playoff round a title names, for "is this the same game" checks across partners: an MLB
 * round by its short name ("ALCS" = "American League Championship Series"), else a final by its
 * words; null for anything else.
 * @param {string | null | undefined} name
 * @returns {string | null}
 */
export const postseasonRound = (name) => {
  const s = String(name ?? '');
  if (/\b(?:college|little league) world series\b/i.test(s)) return null;
  const mlb = s.match(ROUND_ANY);
  if (mlb) return roundOf(mlb[1]);
  const m = s.match(/\b(stanley cup final|(?:nba|wnba) finals|conference finals|mls cup)\b/i);
  return m ? m[1].toLowerCase() : null;
};

/** "American Family Field" = "American-Family Field" */
const placeKey = (/** @type {string | null | undefined} */ s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Where a page made for an MLB postseason placeholder goes (scripts/gen-longtail.mjs, radar
 * rows): the catalog game of the same round on the same day, at the same park or hosted by the
 * same club. Its id, or null when there is none and the page retires (the White Sox's "World
 * Series Home Game 1" after they were knocked out).
 * @param {{ name: string, date: string, venue: string, artist?: string | null }} doc
 * @param {{ id: string, name: string, date: string, venue: string, artist?: string | null }[]} games live catalog games
 * @returns {string | null}
 */
export const placeholderTarget = (doc, games) => {
  const round = postseasonRound(doc.name);
  if (!round) return null;
  const host = mlbNickname(parsePostseason(doc.name, doc)?.home);
  const hit = games.find(
    (g) =>
      g.date === doc.date &&
      postseasonRound(g.name) === round &&
      (placeKey(g.venue) === placeKey(doc.venue) || (!!host && mlbNickname(parsePostseason(g.name, g)?.home) === host))
  );
  return hit?.id ?? null;
};

/** One club, whichever partner wrote it ("Milwaukee Brewers" = "Brewers"). */
const clubKey = (/** @type {string} */ t) => mlbNickname(t) ?? t.trim().toLowerCase();

/**
 * One club in one season's World Series, "2026:Brewers": what a build hands on to the next as
 * "known to be in" (confirmedClubs), so a club that clinched in October 2026 says nothing about 2027.
 * @param {string | null | undefined} date the game's date (YYYY-MM-DD)
 * @param {string} team
 */
export const seasonClub = (date, team) => `${String(date ?? '').slice(0, 4)}:${clubKey(team)}`;

/** @typedef {{ id: string, name: string, date?: string, artist?: string | null }} WsRow */

/** @param {WsRow[]} rows */
const wsGames = (rows) =>
  rows.flatMap((r) => {
    const p = parsePostseason(r.name, r);
    return p?.round === 'World Series' ? [{ id: r.id, date: r.date, p }] : [];
  });

/** "2026" from a game's date */
const seasonOf = (/** @type {string | null | undefined} */ date) => String(date ?? '').slice(0, 4);

/**
 * The clubs known to be in each season's World Series: `confirmed` (seasonClub keys, from earlier
 * builds) and every visitor the games name. Season ("2026") → club keys ("Brewers").
 * @param {ReturnType<typeof wsGames>} games
 * @param {Iterable<string>} confirmed
 */
const inBySeason = (games, confirmed) => {
  /** @type {Map<string, Set<string>>} */
  const known = new Map();
  const add = (/** @type {string} */ key) => {
    const i = key.indexOf(':');
    const season = key.slice(0, i);
    if (!known.has(season)) known.set(season, new Set());
    known.get(season)?.add(key.slice(i + 1));
  };
  for (const k of confirmed) add(k);
  for (const g of games) if (g.p.away) add(seasonClub(g.date, g.p.away));
  return known;
};

/**
 * Clubs known to be in a season's World Series, from World Series games seen: every visitor a
 * game names (SeatGeek names one only once that club is in) and every home team a build marked
 * as in (`pennantPending === false`, scripts/gen-catalog.mjs). A club never un-clinches, so each
 * build hands these on to the next (the last push's games, the series page's record): a partner
 * that drops the games naming a club, or lists the other league's games first, cannot put the
 * club's own games back to "played only if it reaches the World Series".
 * @param {{ name: string, date?: string, artist?: string | null, pennantPending?: boolean }[]} rows any rows; others are ignored
 * @returns {Set<string>} seasonClub keys
 */
export const confirmedClubs = (rows) => {
  const out = new Set();
  for (const r of rows) {
    const p = parsePostseason(r.name, r);
    if (p?.round !== 'World Series') continue;
    if (p.away) out.add(seasonClub(r.date, p.away));
    if (r.pennantPending === false) out.add(seasonClub(r.date, p.home));
  }
  return out;
};

/**
 * World Series games whose HOME team has not clinched its pennant yet: those games are played
 * only if it does. SeatGeek names a visitor only once that club is in — "Brewers at Guardians"
 * while the Guardians still play the ALCS — so a home team counts as in only when a World Series
 * game names it as the visitor (today's, or one an earlier build saw: `confirmed`), or when every
 * game names both sides and they are the same two clubs. Never decided from one game alone.
 * @param {WsRow[]} rows the live games (any rows; others are ignored)
 * @param {{ confirmed?: Set<string> }} [opts] seasonClub keys known to be in (confirmedClubs of earlier builds)
 * @returns {Set<string>} ids
 */
export const pennantPending = (rows, { confirmed = new Set() } = {}) => {
  const games = wsGames(rows);
  const known = new Set([...confirmed, ...games.flatMap((g) => (g.p.away ? [seasonClub(g.date, g.p.away)] : []))]);
  const clubs = new Set(games.flatMap((g) => [g.p.home, g.p.away].filter((x) => !!x).map((x) => clubKey(/** @type {string} */ (x)))));
  const settled = games.length > 0 && games.every((g) => g.p.away) && clubs.size === 2;
  const out = new Set();
  for (const g of games) if (!settled && !known.has(seasonClub(g.date, g.p.home))) out.add(g.id);
  return out;
};

/**
 * World Series games that name no visitor ("TBD at Rays - World Series (Gm 3, HG 1)") but whose
 * opponent is known all the same. A World Series is one club of each league, so once exactly ONE
 * club of the other league is known to be in (named as a visitor, today or by an earlier build:
 * `confirmed`), it is the opponent of every game this league's clubs host, if that game is played
 * at all (pennantPending says when that is not certain). The Brewers clinched the NL on Oct 15, so
 * a Rays home game listed before SeatGeek names its visitor is not "opponent to be decided"
 * (review, 2026-10-19). Two clubs of one league known to be in settle nothing.
 * @param {WsRow[]} rows (any rows; others are ignored)
 * @param {{ confirmed?: Set<string> }} [opts] seasonClub keys known to be in (confirmedClubs of earlier builds)
 * @returns {Map<string, string>} id → the opponent's nickname ("Brewers")
 */
export const knownOpponents = (rows, { confirmed = new Set() } = {}) => {
  const games = wsGames(rows);
  const known = inBySeason(games, confirmed);
  /** @type {Map<string, string>} */
  const out = new Map();
  for (const g of games) {
    const league = mlbLeague(g.p.home);
    if (g.p.away || !league) continue;
    const other = [...(known.get(seasonOf(g.date)) ?? [])].filter((c) => {
      const l = mlbLeague(c);
      return !!l && l !== league;
    });
    if (other.length === 1) out.set(g.id, other[0]);
  }
  return out;
};

/**
 * One build's World Series flags, set on the games themselves (scripts/gen-catalog.mjs). Every
 * World Series game gets `pennantPending` (true = played only if its home team gets in, false =
 * its home team is in); a game that names no visitor gets `wsOpponent` where its opponent is
 * known (knownOpponents); every other event loses both, so no flag outlives the game it was for.
 * The clubs earlier builds knew to be in come from `previous` (the last push's events) and
 * `onRecord` (the series page's list), and they are read BEFORE any flag is cleared: the catalog
 * carries a game missing from today's pull over as the very object the last push's list holds,
 * so clearing first erased the `pennantPending: false` that hands a clinched club on, and the
 * Brewers' carried games read "played only if" again (review, 2026-10-19).
 * @template {WsRow & { category?: string, pennantPending?: boolean, wsOpponent?: string }} E
 * @param {E[]} events this build's events (changed in place)
 * @param {{ previous?: { name: string, date?: string, artist?: string | null, pennantPending?: boolean }[], onRecord?: unknown[] }} [opts]
 * @returns {{ games: E[], pending: Set<string>, opponents: Map<string, string>, confirmed: Set<string> }}
 */
export const markWorldSeries = (events, { previous = [], onRecord = [] } = {}) => {
  const confirmed = confirmedClubs(previous);
  for (const k of onRecord) if (typeof k === 'string') confirmed.add(k);
  const games = events.filter((e) => e.category === 'Sports' && parsePostseason(e.name, e)?.round === 'World Series');
  for (const e of events) {
    delete e.pennantPending;
    delete e.wsOpponent;
  }
  const pending = pennantPending(games, { confirmed });
  const opponents = knownOpponents(games, { confirmed });
  for (const e of games) {
    e.pennantPending = pending.has(e.id);
    const o = opponents.get(e.id);
    if (o) e.wsOpponent = o;
  }
  return { games, pending, opponents, confirmed };
};

/**
 * What is not certain about an MLB postseason game, for its event page (src/lib/notices.ts says
 * it in one line): its opponent (no visitor named and none known: markWorldSeries' `wsOpponent`),
 * whether its home team is in yet (World Series games: the build's `pennantPending`), and whether
 * it is played only if the series is not decided earlier — from its number, unless the exchange's
 * own name says the game is certain (`sure`, set on exchange pages: exchangeSaysPlayed). Null for
 * any other event.
 * @param {{ name: string, artist?: string | null, pennantPending?: boolean, wsOpponent?: string | null, sure?: boolean }} e
 * @returns {{ opponentOpen: boolean, homeNotIn: boolean, ifNecessary: boolean } | null}
 */
export const postseasonDoubts = (e) => {
  const p = parsePostseason(e.name, e);
  if (!p) return null;
  const ws = p.round === 'World Series';
  return { opponentOpen: !p.away && !(ws && e.wsOpponent), homeNotIn: ws && !!e.pennantPending, ifNecessary: p.ifNecessary && e.sure !== true };
};

/**
 * World Series games that cannot be part of this Series: once one club of each league is known
 * to be in (named as a visitor, today or by an earlier build: `confirmed`), a game hosted by any
 * other club is a leftover of a club that is out — the Guardians' games after the Rays clinch,
 * while a partner still lists them or the series page still has them on record. Until both
 * pennants are known nothing is.
 * @param {WsRow[]} rows
 * @param {{ confirmed?: Set<string> }} [opts]
 * @returns {Set<string>} ids
 */
export const outOfSeries = (rows, { confirmed = new Set() } = {}) => {
  const games = wsGames(rows);
  const out = new Set();
  const known = inBySeason(games, confirmed);
  for (const g of games) {
    const clubs = [...(known.get(seasonOf(g.date)) ?? [])];
    const al = clubs.filter((c) => mlbLeague(c) === 'AL');
    const nl = clubs.filter((c) => mlbLeague(c) === 'NL');
    if (al.length === 1 && nl.length === 1 && !clubs.includes(clubKey(g.p.home))) out.add(g.id);
  }
  return out;
};

/** @typedef {{ date?: string, game: number | null, homeGame: number | null, home: string, away?: string | null }} Slotted */
/** A game's place in its series: its number, else its club's home-game number. While two hosts
 *  are possible for one number — the games name two clubs as its host and neither is known to be
 *  in (Games 3-5 at either AL park while the ALCS is open) — the number and its host: a recorded
 *  Rays Game 3 is not the Guardians' Game 3 listed today (review, 2026-10-19: it vanished from the
 *  page instead of saying "not listed today"). Once one of them is known to be in, its listing
 *  replaces the other's recorded game number for number, as before.
 *  @param {Slotted[]} games every game in play (today's and the recorded)
 *  @param {Iterable<string>} confirmed seasonClub keys known to be in (every visitor the games name counts too) */
const slotsOf = (games, confirmed) => {
  const known = new Set(confirmed);
  for (const g of games) if (g.away && mlbNickname(g.away)) known.add(seasonClub(g.date, g.away));
  /** @type {Map<number, Map<string, string>>} game number → host club → season */
  const hosts = new Map();
  for (const g of games) if (g.game) hosts.set(g.game, (hosts.get(g.game) ?? new Map()).set(clubKey(g.home), seasonOf(g.date)));
  const open = (/** @type {number} */ n) => {
    const h = hosts.get(n);
    return !!h && h.size > 1 && ![...h].some(([club, season]) => known.has(`${season}:${club}`));
  };
  return (/** @type {Slotted} */ g) =>
    g.game ? (open(g.game) ? `game ${g.game} at ${clubKey(g.home)}` : `game ${g.game}`) : `${clubKey(g.home)} home game ${g.homeGame}`;
};

/**
 * The series page's games (scripts/gen-insights.mjs): today's listings, plus every game an
 * earlier build recorded this season that is not listed today. A recorded game whose date is
 * behind us stays as the record of what it cost (`past`, its last recorded price and trend); one
 * still ahead says it is not listed today (`delisted`). A listing replaces a recorded game of
 * the same number — a partner's new listing of it, or the club that clinched instead of the
 * recorded one — and, while two hosts are possible for that number, of the same host (slotsOf).
 * Nothing else leaves within a season (outOfSeries still drops a club that is out).
 * @template {{ id: string, date: string, game: number | null, homeGame: number | null, home: string, away?: string | null }} G
 * @param {G[]} listed today's games
 * @param {G[]} recorded the previous build's games, same season
 * @param {string} today YYYY-MM-DD
 * @param {{ confirmed?: Iterable<string> }} [opts] seasonClub keys known to be in (the page's list)
 * @returns {(G & { status: 'upcoming' | 'past' | 'delisted' })[]}
 */
export const seriesRecord = (listed, recorded, today, { confirmed = [] } = {}) => {
  const ids = new Set(listed.map((g) => g.id));
  const slotOf = slotsOf([...listed, ...recorded], confirmed);
  const slots = new Set(listed.map(slotOf));
  return [
    ...listed.map((g) => ({ ...g, status: /** @type {const} */ ('upcoming') })),
    ...recorded
      .filter((g) => !ids.has(g.id) && !slots.has(slotOf(g)))
      .map((g) => ({ ...g, status: g.date < today ? /** @type {const} */ ('past') : /** @type {const} */ ('delisted') })),
  ];
};

/**
 * Games of a World Series (2-3-2) that nobody lists, for the series page: only games still to
 * come — numbered above the earliest game listed today, since games are played in order and a
 * lower number is behind us (the day after Game 1, Game 1 is not "not listed yet") — and whose
 * ballpark they are at: the club the listings name as the visitor of the other park's games, else
 * the other league's champion. Null when nothing is missing, or when no game is listed any more.
 * @param {{ game: number | null, home: string, away: string | null, status: string }[]} games
 * @returns {{ games: number[], host: string | null, league: 'AL' | 'NL' | null } | null}
 */
export const missingGames = (games) => {
  if (!games.length || !games.every((g) => g.game)) return null;
  const listed = games.filter((g) => g.status === 'upcoming').map((g) => /** @type {number} */ (g.game));
  if (!listed.length) return null;
  const first = Math.min(...listed);
  const have = new Set(games.map((g) => g.game));
  const gone = [1, 2, 3, 4, 5, 6, 7].filter((n) => n > first && !have.has(n));
  if (!gone.length) return null;
  // games 1, 2, 6 and 7 are at one club's park, 3 to 5 at the other's: the club hosting the
  // missing ones is the visitor in the other group
  const A = [1, 2, 6, 7];
  const B = [3, 4, 5];
  const group = gone.every((n) => B.includes(n)) ? A : gone.every((n) => A.includes(n)) ? B : null;
  const other = group ? games.find((g) => group.includes(/** @type {number} */ (g.game))) : null;
  const host = other?.away ?? null;
  const known = other ? mlbLeague(other.home) : null;
  const league = host ? mlbLeague(host) : known === 'NL' ? 'AL' : known === 'AL' ? 'NL' : null;
  return { games: gone, host, league };
};
